import apiClient from "./apiClient";
import {
  PATIENT_METADATA_MISMATCH_CODE,
} from "./eegUploadConstants";
import {
  uploadEegResumable,
  shouldUseResumableEegUpload,
} from "./resumableEegUploadClient";
import { supportsDirectBrowserEegUpload } from "./eegUploadCapabilitiesClient";

export {
  DIRECT_EEG_UPLOAD_THRESHOLD_BYTES,
  PATIENT_METADATA_MISMATCH_CODE,
} from "./eegUploadConstants";

const DIRECT_UPLOAD_CONCURRENCY = 4;
const SIGN_PART_BATCH_SIZE = 100;
const PART_UPLOAD_RETRIES = 3;
const PART_UPLOAD_TIMEOUT_MS = 15 * 60 * 1000;

interface DirectUploadInitiatePayload {
  provider: "s3" | "azure_blob";
  bucket: string;
  object_key: string;
  upload_id: string;
  part_size: number;
  expires_in_seconds: number;
  recording_id: string;
  storagepath_id: string;
}

interface DirectUploadSignedPart {
  part_number: number;
  upload_url: string;
  expires_in_seconds: number;
}

interface DirectUploadCompletePayload {
  recording_id: string;
  filename: string;
  file_size: number;
  processing_status: string;
  storage_provider: string;
  storage_key_prefix: string | null;
}

export interface DirectEegUploadResult {
  recordingId: string;
  startDatetime: string | null;
  logicalDurationSeconds: number | null;
  segmentCount: number;
}

export interface PatientMetadataMismatchDetails {
  override_allowed?: boolean;
  expected_patient?: {
    patient_id?: string | null;
    mrn?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  };
  edf_metadata?: {
    patient_id_from_edf?: string | null;
    recording_id_from_edf?: string | null;
  };
}

interface DirectEegUploadOptions {
  studyId: string;
  file: File;
  owningTeamId?: string | null;
  patientId?: string;
  importTaskId?: string;
  onProgress?: (progressPercent: number) => void;
  confirmPatientMetadataMismatch?: (
    filename: string,
    details: PatientMetadataMismatchDetails,
  ) => Promise<boolean>;
}

interface ApiUploadRecordingPayload {
  recording_id: string;
  start_datetime?: string | null;
  duration_seconds?: number | null;
}

interface ApiErrorPayload {
  code?: string;
  message?: string;
  details?: PatientMetadataMismatchDetails;
}

const isPatientMetadataMismatch = (error: unknown): ApiErrorPayload | null => {
  const payload = (error as { response?: { data?: ApiErrorPayload } })?.response
    ?.data;
  if (
    (error as { response?: { status?: number } })?.response?.status === 409 &&
    payload?.code === PATIENT_METADATA_MISMATCH_CODE &&
    payload.details?.override_allowed
  ) {
    return payload;
  }
  return null;
};

const defaultConfirmPatientMetadataMismatch = async (
  filename: string,
  details: PatientMetadataMismatchDetails,
): Promise<boolean> => {
  const expectedName = [
    details.expected_patient?.first_name,
    details.expected_patient?.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const expectedLabel = expectedName || "the selected patient";
  const expectedMrn = details.expected_patient?.mrn
    ? ` (MRN: ${details.expected_patient.mrn})`
    : "";
  const edfPatient = details.edf_metadata?.patient_id_from_edf;
  const edfRecording = details.edf_metadata?.recording_id_from_edf;
  const message = [
    `"${filename}" appears to belong to a different patient than ${expectedLabel}${expectedMrn}.`,
    "",
    ...(edfPatient ? [`EDF patient field: ${edfPatient}`] : []),
    ...(edfRecording ? [`EDF recording field: ${edfRecording}`] : []),
    "",
    "Upload anyway with override?",
  ].join("\n");
  return window.confirm(message);
};

const uploadEegThroughApi = async ({
  studyId,
  file,
  owningTeamId,
  onProgress,
  confirmPatientMetadataMismatch,
}: DirectEegUploadOptions): Promise<DirectEegUploadResult> => {
  const postUpload = async (allowPatientMismatchOverride: boolean) => {
    const formData = new FormData();
    formData.append("eegFile", file, file.name);
    formData.append("study_id", studyId);
    if (owningTeamId) {
      formData.append("owning_team_id", owningTeamId);
    }
    if (allowPatientMismatchOverride) {
      formData.append("patient_mismatch_override", "true");
    }
    return apiClient.post<{
      data: ApiUploadRecordingPayload;
    }>("/eeg/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        if (event.total) {
          onProgress?.(Math.min(99, Math.round((event.loaded * 100) / event.total)));
        }
      },
    });
  };

  const resolveUploadResponse = (response: {
    data?: { data?: ApiUploadRecordingPayload };
  }) => {
    const recording = response.data?.data;
    if (!recording?.recording_id) {
      throw new Error("EEG upload completed but no recording_id was returned.");
    }
    onProgress?.(100);
    return {
      recordingId: recording.recording_id,
      startDatetime: recording.start_datetime || null,
      logicalDurationSeconds: recording.duration_seconds ?? null,
      segmentCount: 1,
    };
  };

  try {
    const response = await postUpload(false);
    return resolveUploadResponse(response);
  } catch (error: unknown) {
    const mismatch = isPatientMetadataMismatch(error);
    if (!mismatch?.details) {
      throw error;
    }
    const confirmMismatch =
      confirmPatientMetadataMismatch || defaultConfirmPatientMetadataMismatch;
    const confirmed = await confirmMismatch(file.name, mismatch.details);
    if (!confirmed) {
      throw new Error("Patient metadata override declined by user.");
    }
    const overrideResponse = await postUpload(true);
    return resolveUploadResponse(overrideResponse);
  }
};

const wait = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const formatPartFailure = (status: number, responseText: string) => {
  if (status === 0) {
    return "S3 upload part failed before a response was received. Check network access and S3 CORS for the portal origin.";
  }
  const body = responseText?.trim();
  return body
    ? `S3 upload part failed with HTTP ${status}: ${body.slice(0, 240)}`
    : `S3 upload part failed with HTTP ${status}`;
};

const uploadPartWithProgress = (
  uploadUrl: string,
  blob: Blob,
  onProgress: (loaded: number) => void,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.timeout = PART_UPLOAD_TIMEOUT_MS;
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag");
        if (!etag) {
          reject(new Error("S3 upload part completed without an ETag."));
          return;
        }
        onProgress(blob.size);
        resolve(etag);
        return;
      }
      reject(new Error(formatPartFailure(xhr.status, xhr.responseText)));
    };
    xhr.onerror = () =>
      reject(new Error(formatPartFailure(xhr.status, xhr.responseText)));
    xhr.ontimeout = () => reject(new Error("S3 upload part timed out."));
    xhr.send(blob);
  });

const uploadPartWithRetry = async (
  signedPart: DirectUploadSignedPart,
  blob: Blob,
  onProgress: (loaded: number) => void,
) => {
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= PART_UPLOAD_RETRIES; attempt += 1) {
    try {
      return await uploadPartWithProgress(signedPart.upload_url, blob, onProgress);
    } catch (error) {
      lastError = error;
      onProgress(0);
      if (attempt < PART_UPLOAD_RETRIES) {
        await wait(500 * 2 ** (attempt - 1));
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error(`S3 upload part ${signedPart.part_number} failed.`);
};

const signUploadParts = async (
  studyId: string,
  objectKey: string,
  uploadId: string,
  totalParts: number,
) => {
  const signedParts = new Map<number, DirectUploadSignedPart>();
  const partNumbers = Array.from({ length: totalParts }, (_, index) => index + 1);

  for (let index = 0; index < partNumbers.length; index += SIGN_PART_BATCH_SIZE) {
    const batch = partNumbers.slice(index, index + SIGN_PART_BATCH_SIZE);
    const response = await apiClient.post<{
      data: { parts: DirectUploadSignedPart[] };
    }>("/eeg/direct-upload/sign-parts", {
      study_id: studyId,
      object_key: objectKey,
      upload_id: uploadId,
      part_numbers: batch,
    });
    response.data?.data?.parts?.forEach((part) => {
      signedParts.set(part.part_number, part);
    });
  }

  if (signedParts.size !== totalParts) {
    throw new Error("Direct upload signing did not return every requested part.");
  }

  return signedParts;
};

export const uploadEegDirectToStorage = async ({
  studyId,
  file,
  owningTeamId,
  patientId,
  importTaskId,
  onProgress,
  confirmPatientMetadataMismatch,
}: DirectEegUploadOptions): Promise<DirectEegUploadResult> => {
  const directBrowserUploadSupported = await supportsDirectBrowserEegUpload();
  if (!directBrowserUploadSupported) {
    if (shouldUseResumableEegUpload(file.size)) {
      return uploadEegResumable({
        studyId,
        file,
        owningTeamId,
        patientId,
        importTaskId,
        onProgress,
        confirmPatientMetadataMismatch,
      });
    }
    return uploadEegThroughApi({
      studyId,
      file,
      owningTeamId,
      onProgress,
      confirmPatientMetadataMismatch,
    });
  }

  let initiateResponse;
  try {
    initiateResponse = await apiClient.post<{
      data: DirectUploadInitiatePayload;
    }>("/eeg/direct-upload/initiate", {
      study_id: studyId,
      filename: file.name,
      size_bytes: file.size,
      content_type: file.type || "application/octet-stream",
    });
  } catch (error: any) {
    if (error?.response?.status === 409) {
      if (shouldUseResumableEegUpload(file.size)) {
        return uploadEegResumable({
          studyId,
          file,
          owningTeamId,
          patientId,
          importTaskId,
          onProgress,
          confirmPatientMetadataMismatch,
        });
      }
      return uploadEegThroughApi({
        studyId,
        file,
        owningTeamId,
        onProgress,
        confirmPatientMetadataMismatch,
      });
    }
    throw error;
  }
  const session = initiateResponse.data?.data;
  if (!session?.upload_id || !session.object_key || !session.part_size) {
    throw new Error("Direct upload initiation did not return an upload session.");
  }

  const partSize = session.part_size;
  const totalParts = Math.ceil(file.size / partSize);
  const uploadedByPart = new Map<number, number>();
  const completedParts: Array<{ part_number: number; etag: string }> = [];
  const reportProgress = () => {
    const loaded = Array.from(uploadedByPart.values()).reduce(
      (sum, value) => sum + value,
      0,
    );
    onProgress?.(Math.min(99, Math.round((loaded * 100) / file.size)));
  };

  const abortUpload = async () => {
    await apiClient
      .post("/eeg/direct-upload/abort", {
        study_id: studyId,
        object_key: session.object_key,
        upload_id: session.upload_id,
      })
      .catch(() => undefined);
  };

  try {
    const signedParts = await signUploadParts(
      studyId,
      session.object_key,
      session.upload_id,
      totalParts,
    );
    let nextPart = 1;
    const workers = Array.from({
      length: Math.min(DIRECT_UPLOAD_CONCURRENCY, totalParts),
    }).map(async () => {
      while (nextPart <= totalParts) {
        const partNumber = nextPart;
        nextPart += 1;
        const signedPart = signedParts.get(partNumber);
        if (!signedPart?.upload_url) {
          throw new Error(`Direct upload part ${partNumber} was not signed.`);
        }
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const etag = await uploadPartWithRetry(
          signedPart,
          file.slice(start, end),
          (loaded) => {
            uploadedByPart.set(partNumber, loaded);
            reportProgress();
          },
        );
        completedParts.push({ part_number: partNumber, etag });
      }
    });
    await Promise.all(workers);
    completedParts.sort((left, right) => left.part_number - right.part_number);

    const completeResponse = await apiClient.post<{
      data: DirectUploadCompletePayload;
    }>("/eeg/direct-upload/complete", {
      study_id: studyId,
      filename: file.name,
      object_key: session.object_key,
      upload_id: session.upload_id,
      recording_id: session.recording_id,
      owning_team_id: owningTeamId || undefined,
      parts: completedParts,
    });
    const recordingId = completeResponse.data?.data?.recording_id;
    if (!recordingId) {
      throw new Error("Direct upload completed but no recording_id was returned.");
    }

    onProgress?.(100);
    return {
      recordingId,
      startDatetime: null,
      logicalDurationSeconds: null,
      segmentCount: 1,
    };
  } catch (error) {
    await abortUpload();
    throw error;
  }
};
